import React, { useCallback, useEffect } from "react";

import Located from "@mapmycustomers/shared/types/base/Located";
import { convertCoordinatesToLatLngLiteral } from "@mapmycustomers/shared/util/geo/GeoService";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import useRerenderer from "@mapmycustomers/shared/util/hook/useRerenderer";

import { useMap } from "@app/component/map/FeaturedMap";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useMapEventHandler from "@app/util/hook/map/useMapEventHandler";
import useMapProjection from "@app/util/hook/map/useMapProjection";

import DropPin from "./DropPin";
import LeadPopover from "./LeadPopover";
import styles from "./PinPopover.module.scss";

interface Props {
  address?: Located;
  coordinates?: GeolocationCoordinates;
  loading?: boolean;
  onCancel: () => void;
  onCreateCompany: () => void;
  onCreatePerson: () => void;
  onMenuItemClick?: () => void;
  placeResult?: google.maps.places.PlaceResult;
  title: string;
  visible?: boolean;
}

const PinPopover: React.FC<Props> = ({
  address,
  coordinates,
  loading,
  onCancel,
  onCreateCompany,
  onCreatePerson,
  onMenuItemClick,
  placeResult,
  title,
  visible,
}) => {
  const { map } = useMap();
  const analyticIssuer = useExtendedAnalytics([placeResult ? "Lead" : "POI"], useAnalytics());

  const [dragging, setDragging, unsetDragging] = useBoolean();
  const forceRender = useRerenderer();

  useMapEventHandler(map, "dragstart", setDragging);
  useMapEventHandler(map, "dragend", unsetDragging);
  useMapEventHandler(
    map,
    "bounds_changed",
    useDebouncedCallback([
      function (this: google.maps.Map) {
        if (visible) {
          forceRender();
        }
      },
      100,
    ])
  );

  const isPopoverVisible = visible && !dragging && coordinates;

  useEffect(() => {
    if (isPopoverVisible) {
      analyticIssuer.clicked([]);
    }
  }, [analyticIssuer, isPopoverVisible]);

  const handleCreateCompany = useCallback(() => {
    analyticIssuer.clicked(["Create as Company"]);
    onCreateCompany();
  }, [analyticIssuer, onCreateCompany]);

  const handleCreatePerson = useCallback(() => {
    analyticIssuer.clicked(["Create as Person"]);
    onCreatePerson();
  }, [analyticIssuer, onCreatePerson]);

  const popoverPosition = useMapProjection(
    map,
    map?.getBounds(),
    map?.getProjection(),
    coordinates ? convertCoordinatesToLatLngLiteral(coordinates) : undefined,
    visible
  );

  return (
    <AnalyticsContext.Provider value={analyticIssuer}>
      <div className={styles.container} style={popoverPosition}>
        {isPopoverVisible ? (
          placeResult ? (
            <LeadPopover
              address={address}
              coordinates={coordinates}
              loading={loading}
              onCancel={onCancel}
              onCreateCompany={handleCreateCompany}
              onCreatePerson={handleCreatePerson}
              placeResult={placeResult}
              title={title}
            />
          ) : (
            <DropPin
              address={address}
              coordinates={coordinates}
              loading={loading}
              onCancel={onCancel}
              onCreateCompany={handleCreateCompany}
              onCreatePerson={handleCreatePerson}
              onMenuItemClick={onMenuItemClick}
              title={title}
            />
          )
        ) : null}
      </div>
    </AnalyticsContext.Provider>
  );
};

export default PinPopover;
